import { FC } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import type { IncompleteRole } from "./profileSetup";

interface ProfileSetupWarningProps {
  incomplete: IncompleteRole;
  /** Shown before the reason when the surrounding context doesn't already name the role. */
  roleName?: string;
  isSelfView: boolean;
  onEditProperties: () => void;
}

/**
 * Per-role action-required warning naming exactly what failed (CREW-530).
 * Positions tab renders it under the role heading (no roleName); the
 * Operations tab renders it in a top block with the role name inlined,
 * because positions without operations produce no groups there.
 */
const ProfileSetupWarning: FC<ProfileSetupWarningProps> = ({ incomplete, roleName, isSelfView, onEditProperties }) => {
  const { t } = useTranslation();

  const reasonText =
    incomplete.reason === "missing_positions"
      ? t("compliance.profile_setup_missing_positions")
      : t("compliance.profile_setup_missing_operations", {
          positions: incomplete.positionsMissingOperations.map((position) => position.name).join(", "),
        });

  return (
    <Alert severity="warning" sx={{ mb: 1 }}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontWeight: 600,
        }}
      >
        {t("compliance.action_required")}:{" "}
      </Typography>
      {roleName && (
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 600,
          }}
        >
          {roleName} —{" "}
        </Typography>
      )}
      <Typography variant="body2" component="span">
        {reasonText}{" "}
      </Typography>
      {isSelfView ? (
        <Typography variant="body2" component="span">
          {t("compliance.profile_setup_self_body")}
        </Typography>
      ) : (
        <Link component="button" variant="body2" onClick={onEditProperties}>
          {t("compliance.edit_user_properties")}
        </Link>
      )}
    </Alert>
  );
};

export default ProfileSetupWarning;
